// aiClient.js — Cliente único para las llamadas a la IA (/api/anthropic).
//
// Por qué existe: cada página repetía a mano el mismo andamiaje (headers con
// token, body con model/max_tokens/messages, parseo de data.content, extracción
// del JSON con regex y manejo de errores). Esa duplicación causó inconsistencias
// y bugs de extracción. Centralizar deja un solo lugar para el manejo de errores
// y el parseo, y un solo punto donde ajustar modelo o límites.

const TOKEN_KEY = 'nebb_token';

function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Llama a la IA y devuelve el TEXTO plano de la respuesta.
 *
 * @param {Object} opts
 * @param {string|Array} opts.content  El mensaje del usuario: puede ser un
 *   string simple, o un array de bloques (para enviar PDFs/imágenes en base64).
 * @param {string} [opts.model]        Modelo. Default: Haiku (rápido/barato).
 * @param {number} [opts.maxTokens]    Límite de tokens. Default 4000.
 * @returns {Promise<string>} texto de la respuesta (concatenado y trimmeado).
 * @throws {Error} si la API devuelve error o no hay respuesta.
 */
export async function pedirTextoIA({ content, model = 'claude-haiku-4-5-20251001', maxTokens = 4000, permitirTruncado = false }) {
  const response = await fetch('/api/anthropic', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      model,
      max_tokens: maxTokens,
      messages: [{ role: 'user', content }],
    }),
  });

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('No se pudo leer la respuesta de la IA. Intentá de nuevo.');
  }

  if (!response.ok || data?.error) {
    const msg = data?.error?.message || data?.error || `Error de la IA (${response.status})`;
    throw new Error(msg);
  }

  // Si la respuesta se cortó por límite de tokens, el contenido (y un JSON
  // embebido) puede estar incompleto. Por defecto avisamos con un error claro;
  // si el llamador quiere intentar recuperar lo parcial, pasa permitirTruncado.
  if (data.stop_reason === 'max_tokens' && !permitirTruncado) {
    const e = new Error('La respuesta quedó incompleta por su tamaño (límite de tokens). Probá dividir el documento o cargar lo que falta a mano.');
    e.code = 'max_tokens';
    throw e;
  }

  const texto = (data.content || []).map(b => b.text || '').join('').trim();
  if (!texto && !permitirTruncado) throw new Error('La IA no devolvió contenido. Intentá de nuevo.');
  return { texto, truncado: data.stop_reason === 'max_tokens' };
}

/**
 * Llama a la IA y devuelve el primer JSON (objeto o array) que aparezca en la
 * respuesta. Tolera que el modelo agregue texto o backticks alrededor.
 *
 * @param {Object} opts  Igual que pedirTextoIA, más:
 * @param {'object'|'array'|'any'} [opts.esperado]  Qué forma esperar. 'array'
 *   busca [...], 'object' busca {...}, 'any' (default) prueba primero array.
 * @returns {Promise<any>} el JSON ya parseado.
 * @throws {Error} si no hay JSON válido en la respuesta.
 */
export async function pedirJSONIA({ esperado = 'any', ...opts }) {
  const { texto } = await pedirTextoIA(opts);
  return extraerJSON(texto, esperado);
}

// Extrae y parsea el primer JSON del texto. Robusto ante backticks y prosa.
export function extraerJSON(texto, esperado = 'any') {
  const limpio = texto.replace(/```json/gi, '').replace(/```/g, '').trim();

  const tryParse = (str) => { try { return JSON.parse(str); } catch { return undefined; } };

  // Intento directo (cuando la IA devolvió SOLO el JSON)
  const directo = tryParse(limpio);
  if (directo !== undefined) return directo;

  // Buscar array y objeto embebidos
  const arrMatch = limpio.match(/\[[\s\S]*\]/);
  const objMatch = limpio.match(/\{[\s\S]*\}/);

  const candidatos = [];
  if (esperado === 'array') {
    if (arrMatch) candidatos.push(arrMatch[0]);
  } else if (esperado === 'object') {
    if (objMatch) candidatos.push(objMatch[0]);
  } else {
    // 'any': probar array primero (varios sistemas devuelven listas), luego objeto
    if (arrMatch) candidatos.push(arrMatch[0]);
    if (objMatch) candidatos.push(objMatch[0]);
  }

  for (const c of candidatos) {
    const parsed = tryParse(c);
    if (parsed !== undefined) return parsed;
  }

  throw new Error('La IA no devolvió un JSON válido. Intentá de nuevo.');
}
